import type { QueryClient } from '@tanstack/react-query'
import type { ExportJob, ExportStatus } from '../api/contracts'
import { ALL_EXPORTS_KEY, exportsQueryKeys } from '../api/queryKeys'

/**
 * Every Recent Exports list the page holds (a table's, a list's, the
 * header's) and every export the watcher reads, kept in step: an export's
 * state only moves forward, and whichever read sees it move first moves it
 * everywhere at once. So the Export button's dot and the header's never
 * disagree or flicker while their lists are read at different moments.
 */

type ListData = { exports: ExportJob[] }

const LISTS = ['exports', 'list'] as const

// How far along each status is: an older read never takes an export back.
const STAGE: Record<ExportStatus, number> = {
  queued: 0,
  running: 1,
  done: 2,
  failed: 2,
  canceled: 2,
}

/** The further along of two reads of one export (the newer when they're level). */
export function furtherExport(known: ExportJob, read: ExportJob): ExportJob {
  const ahead =
    STAGE[read.status] > STAGE[known.status] ||
    (STAGE[read.status] === STAGE[known.status] && read.rows_written >= known.rows_written)
  const latest = ahead ? read : known
  // Seen stays seen: marking it can land before a read that started earlier.
  const seenAt = latest.seen_at ?? known.seen_at ?? read.seen_at ?? null
  return seenAt === latest.seen_at ? latest : { ...latest, seen_at: seenAt }
}

function knownExports(queryClient: QueryClient): Map<string, ExportJob> {
  const known = new Map<string, ExportJob>()
  const note = (job: ExportJob) => {
    const other = known.get(job.export_id)
    known.set(job.export_id, other ? furtherExport(other, job) : job)
  }
  for (const [, data] of queryClient.getQueriesData<ListData>({ queryKey: LISTS })) {
    data?.exports.forEach(note)
  }
  for (const [, job] of queryClient.getQueriesData<ExportJob>({
    queryKey: ['exports', 'job'],
  })) {
    if (job) note(job)
  }
  return known
}

/** A list just read, with each export as far along as anything on the page has seen it. */
export function withKnownExports(queryClient: QueryClient, jobs: ExportJob[]): ExportJob[] {
  const known = knownExports(queryClient)
  return jobs.map((job) => {
    const other = known.get(job.export_id)
    return other ? furtherExport(other, job) : job
  })
}

/** Move these exports forward in every list that has them (a list without them is left as is). */
export function syncExports(queryClient: QueryClient, jobs: ExportJob[]): void {
  if (jobs.length === 0) return
  const byId = new Map(jobs.map((job) => [job.export_id, job]))
  queryClient.setQueriesData<ListData>({ queryKey: LISTS }, (current) => {
    if (!current) return current
    let changed = false
    const exports = current.exports.map((job) => {
      const read = byId.get(job.export_id)
      if (!read) return job
      const next = furtherExport(job, read)
      if (next !== job) changed = true
      return next
    })
    return changed ? { exports } : current
  })
}

/** A just-queued export, at the top of its own list and the header's. */
export function addExport(queryClient: QueryClient, scopeKey: string, job: ExportJob): void {
  for (const key of new Set([scopeKey, ALL_EXPORTS_KEY])) {
    queryClient.setQueryData<ListData>(exportsQueryKeys.list(key), (current) => ({
      exports: [
        job,
        ...(current?.exports ?? []).filter((other) => other.export_id !== job.export_id),
      ],
    }))
  }
}

/** Mark these exports seen in every list at once. */
export function markSeen(queryClient: QueryClient, exportIds: string[], seenAt: string): void {
  if (exportIds.length === 0) return
  const ids = new Set(exportIds)
  queryClient.setQueriesData<ListData>({ queryKey: LISTS }, (current) => {
    if (!current || !current.exports.some((job) => ids.has(job.export_id) && !job.seen_at)) {
      return current
    }
    return {
      exports: current.exports.map((job) =>
        ids.has(job.export_id) && !job.seen_at ? { ...job, seen_at: seenAt } : job
      ),
    }
  })
}
